import { Box, Button, HStack, Text } from '@chakra-ui/react';
import { FilterIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { forwardRef } from 'react';

export type SearchFilterTriggerProps = {
  /** Number of selected filter values (CE + KB tags, etc.). */
  activeFilterCount: number;
  onClick: () => void;
  disabled?: boolean;
};

/**
 * Opens the search filter modal.
 * Badge shows selected count (capped at 99+).
 */
export const SearchFilterTrigger = forwardRef<HTMLButtonElement, SearchFilterTriggerProps>(
  ({ activeFilterCount, onClick, disabled = false }, ref): JSX.Element => {
    const isActive = activeFilterCount > 0;
    const displayCount = activeFilterCount > 99 ? '99+' : String(activeFilterCount);

    const ariaLabel =
      activeFilterCount > 0
        ? `Filter öffnen, ${activeFilterCount} ausgewählte Filter`
        : 'Filter öffnen';

    return (
      <Button
        ref={ref}
        type="button"
        variant="secondary"
        size="l"
        disabled={disabled}
        position="relative"
        borderColor={isActive ? 'border.accent.active' : undefined}
        _hover={
          disabled
            ? undefined
            : {
                bg: isActive ? 'background.subtle.green' : 'background.primary',
                borderColor: isActive ? 'border.accent.active' : 'border.accent.hover',
              }
        }
        aria-label={ariaLabel}
        onClick={onClick}
      >
        <HStack gap="10px" alignItems="center">
          <NovaIcon icon={FilterIcon} size="m" color="currentColor" aria-hidden />
          Filter
        </HStack>
        {isActive ? (
          <Box
            position="absolute"
            top="-4px"
            right="-4px"
            minW="18px"
            h="18px"
            px="6px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="full"
            bg="background.error"
            color="text.onAccent"
            aria-hidden
          >
            <Text textStyle="body.xs.medium" color="text.onAccent">
              {displayCount}
            </Text>
          </Box>
        ) : null}
      </Button>
    );
  }
);

SearchFilterTrigger.displayName = 'SearchFilterTrigger';
